import { useState, useEffect, useCallback } from 'react'
import type { Client } from '../types'

export function useClients() {
  const [clients, setClients] = useState<Client[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchClients = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch('/api/clients')
      if (!response.ok) throw new Error('Failed to fetch clients')
      const data = await response.json()
      setClients(data.clients || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchClients()
  }, [fetchClients])

  const getClient = async (email: string): Promise<Client> => {
    const res = await fetch(`/api/clients/${encodeURIComponent(email)}`)
    if (!res.ok) throw new Error('Failed to fetch client')
    return res.json()
  }

  const createClient = async (client: { email: string; name: string; role?: string }) => {
    const res = await fetch('/api/clients', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(client),
    })
    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.error || 'Failed to create client')
    }
    await fetchClients()
    return res.json()
  }

  const updateClient = async (email: string, updates: Partial<Client>) => {
    const res = await fetch(`/api/clients/${encodeURIComponent(email)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    })
    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.error || 'Failed to update client')
    }
    await fetchClients()
    return res.json()
  }

  const deleteClient = async (email: string) => {
    const res = await fetch(`/api/clients/${encodeURIComponent(email)}`, {
      method: 'DELETE',
    })
    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.error || 'Failed to delete client')
    }
    await fetchClients()
  }

  return { clients, loading, error, refetch: fetchClients, getClient, createClient, updateClient, deleteClient }
}
